import { get, patch, post } from './client.ts'
import type {
  Substation,
  Feeder,
  Transformer,
  Meter,
  TraceResult,
  TopologyStats,
  DownstreamMembers,
  BulkVerifyRequest,
  EntityHistoryEntry,
} from '../types/topology.ts'
import type { FeederVerificationProgress } from '../types/dataQuality.ts'

// Substations
export function getSubstations(params?: { skip?: number; limit?: number }): Promise<Substation[]> {
  const qs = new URLSearchParams()
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Substation[]>(`/substations${q ? `?${q}` : ''}`)
}

export function getSubstation(id: number): Promise<Substation> {
  return get<Substation>(`/substations/${id}`)
}

// Feeders
export function getFeeders(params?: { substation_id?: number; skip?: number; limit?: number }): Promise<Feeder[]> {
  const qs = new URLSearchParams()
  if (params?.substation_id) qs.set('substation_id', String(params.substation_id))
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Feeder[]>(`/feeders${q ? `?${q}` : ''}`)
}

export function getFeeder(id: number): Promise<Feeder> {
  return get<Feeder>(`/feeders/${id}`)
}

// Transformers
export function getTransformers(params?: {
  feeder_id?: number
  status?: string
  skip?: number
  limit?: number
}): Promise<Transformer[]> {
  const qs = new URLSearchParams()
  if (params?.feeder_id) qs.set('feeder_id', String(params.feeder_id))
  if (params?.status) qs.set('verification_status', params.status)
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Transformer[]>(`/transformers${q ? `?${q}` : ''}`)
}

export function getTransformer(id: number): Promise<Transformer> {
  return get<Transformer>(`/transformers/${id}`)
}

// Meters
export function getMeters(params?: {
  transformer_id?: number
  status?: string
  skip?: number
  limit?: number
}): Promise<Meter[]> {
  const qs = new URLSearchParams()
  if (params?.transformer_id) qs.set('transformer_id', String(params.transformer_id))
  if (params?.status) qs.set('verification_status', params.status)
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Meter[]>(`/meters${q ? `?${q}` : ''}`)
}

export function getMeter(id: number): Promise<Meter> {
  return get<Meter>(`/meters/${id}`)
}

// Verification — backend expects query parameters, not body
export function verifyMeter(id: number, data: {
  verification_status: string
  verification_notes?: string
}): Promise<Meter> {
  const qs = new URLSearchParams({ status: data.verification_status })
  if (data.verification_notes) qs.set('notes', data.verification_notes)
  return patch<Meter>(`/meters/${id}/verify?${qs.toString()}`)
}

export function verifyTransformer(id: number, data: {
  verification_status: string
  verification_notes?: string
}): Promise<Transformer> {
  const qs = new URLSearchParams({ status: data.verification_status })
  if (data.verification_notes) qs.set('notes', data.verification_notes)
  return patch<Transformer>(`/transformers/${id}/verify?${qs.toString()}`)
}

// Bulk verify many meters or transformers in one transaction (item 4).
export function bulkVerify(body: BulkVerifyRequest): Promise<{ verified: number }> {
  return post<{ verified: number }>('/verify/bulk', body)
}

// "Verify all meters on this transformer" (item 4). status/notes are query
// params, matching the backend endpoint.
export function verifyMetersOnTransformer(
  transformerId: number,
  status: string,
  notes?: string,
): Promise<{ verified: number }> {
  const qs = new URLSearchParams({ status })
  if (notes) qs.set('notes', notes)
  return post<{ verified: number }>(`/transformers/${transformerId}/verify-meters?${qs.toString()}`)
}

// Per-feeder verification progress (meters + transformers on the feeder).
export function getFeederVerification(feederId: number): Promise<FeederVerificationProgress> {
  return get<FeederVerificationProgress>(`/feeders/${feederId}/verification`)
}

// Audit history (UX C4, spec 3.11) — newest first, limit 20 server-side.
export function getMeterHistory(id: number): Promise<EntityHistoryEntry[]> {
  return get<EntityHistoryEntry[]>(`/meters/${id}/history`)
}

export function getTransformerHistory(id: number): Promise<EntityHistoryEntry[]> {
  return get<EntityHistoryEntry[]>(`/transformers/${id}/history`)
}

// Unlinked work queues (endpoints existed unconsumed until item 4).
export function getUnlinkedTransformers(params?: {
  skip?: number
  limit?: number
}): Promise<Transformer[]> {
  const qs = new URLSearchParams()
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Transformer[]>(`/transformers/unlinked${q ? `?${q}` : ''}`)
}

export function getUnlinkedMeters(params?: {
  skip?: number
  limit?: number
}): Promise<Meter[]> {
  const qs = new URLSearchParams()
  if (params?.skip) qs.set('skip', String(params.skip))
  if (params?.limit) qs.set('limit', String(params.limit))
  const q = qs.toString()
  return get<Meter[]>(`/meters/unlinked${q ? `?${q}` : ''}`)
}

/** Assign a meter to a transformer.
 *
 *  `expectedCurrentTransformerId` is an optional optimistic-concurrency
 *  precondition (UX C4): pass the transformer the meter is expected to STILL be
 *  on, and the server answers 409 if someone else re-parented it meanwhile —
 *  which is exactly the window a 30 s undo leaves open. Omit it and the write
 *  is unconditional, exactly as before. */
export function assignMeter(
  meterId: number,
  transformerId: number,
  opts?: { expectedCurrentTransformerId?: number },
): Promise<Meter> {
  const params = new URLSearchParams({ transformer_id: String(transformerId) })
  if (opts?.expectedCurrentTransformerId != null) {
    params.set(
      'expected_current_transformer_id',
      String(opts.expectedCurrentTransformerId),
    )
  }
  return patch<Meter>(`/meters/${meterId}/assign?${params.toString()}`)
}

/** Assign a transformer to a feeder. `expectedCurrentFeederId` is the same
 *  optional precondition as above (409 on mismatch). */
export function assignTransformerToFeeder(
  transformerId: number,
  feederId: number,
  opts?: { expectedCurrentFeederId?: number },
): Promise<Transformer> {
  const params = new URLSearchParams({ feeder_id: String(feederId) })
  if (opts?.expectedCurrentFeederId != null) {
    params.set('expected_current_feeder_id', String(opts.expectedCurrentFeederId))
  }
  return patch<Transformer>(
    `/transformers/${transformerId}/assign-feeder?${params.toString()}`,
  )
}

// Trace
export function traceMeter(meterId: number): Promise<TraceResult> {
  return get<TraceResult>(`/meters/${meterId}/trace`)
}

// Downstream members for the map highlight (spec §4.3)
export function getTransformerDownstream(id: number): Promise<DownstreamMembers> {
  return get<DownstreamMembers>(`/transformers/${id}/downstream`)
}

export function getDeviceDownstream(id: number): Promise<DownstreamMembers> {
  return get<DownstreamMembers>(`/devices/${id}/downstream`)
}

// Stats
export function getTopologyStats(): Promise<TopologyStats> {
  return get<TopologyStats>('/stats')
}
